import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { MagnifyingGlass, X } from '@phosphor-icons/react'

const ASYNC_SEARCH_DEBOUNCE_MS = 300

const normalizeSearch = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

/**
 * Lista compartida por el filtro rápido, el panel lateral y los filtros por grupos.
 * `singleSelect` muestra radios; por defecto muestra checkboxes para selección múltiple.
 * La selección pertenece al padre: esta lista sólo notifica cambios mediante `onToggle`.
 *
 * Sin `onSearchChange`, busca localmente por etiqueta ignorando mayúsculas y acentos.
 * Con ese callback, el padre consulta y entrega `options`; la lista no vuelve a filtrarlas.
 * `searchOnEnter` permite consultar sólo al pulsar Enter; limpiar busca de inmediato con ''.
 *
 * En selección múltiple, "Seleccionar esta lista" afecta sólo las opciones visibles.
 * `globalSelection` representa todo el conjunto remoto sin enumerar sus páginas:
 * `mode: 'all', explicit: true` incluye todo y `mode: 'exclude'` guarda sólo las excepciones.
 */
export default function FilterUbiquaOptionList({
  filterKey,
  disabled = false,
  optionsDisabled = false,
  showLoadingIndicator = true,
  singleSelect = false,
  options = [],
  selectedValues = [],
  globalSelection = false,
  selectAllLabel = 'Seleccionar todos del período',
  selection,
  searchPlaceholder = 'Buscar…',
  showSearch = true,
  autoFocus = false,
  emptyLabel = 'No hay opciones que coincidan.',
  onToggle,
  onClear,
  onSelectAllVisible,
  onSearchChange,
  initialSearchQuery = '',
  remoteSearchQuery = '',
  searchOnEnter = false,
  onLoadMore,
  hasMore = false,
  error,
  onRetry,
  loading = false
}) {
  const radioGroupId = useId()
  const [query, setQuery] = useState('')
  const initialRemoteSearchRef = useRef(
    String(remoteSearchQuery || initialSearchQuery || '').trim()
  )
  const lastSearchRef = useRef(initialRemoteSearchRef.current)
  const loadRequestedRef = useRef(false)
  const searchTimeoutRef = useRef(null)
  const onSearchChangeRef = useRef(onSearchChange)
  onSearchChangeRef.current = onSearchChange
  const asyncSearch = typeof onSearchChange === 'function'
  const submitAsyncSearch = (value) => {
    if (!asyncSearch) return
    const normalizedQuery = value.trim()
    // Enter o espacios adicionales no deben repetir la última consulta enviada.
    if (lastSearchRef.current === normalizedQuery) return
    lastSearchRef.current = normalizedQuery
    onSearchChangeRef.current?.(normalizedQuery)
  }

  const cancelPendingSearch = () => {
    if (searchTimeoutRef.current !== null) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }
  }

  /**
   * Espera una pausa de 300 ms para evitar una consulta remota por cada tecla.
   * Cada cambio cancela el envío pendiente y conserva sólo el texto más reciente.
   * Enter y limpiar cancelan esta espera; desmontar también cancela el temporizador.
   * Esto cancela envíos pendientes, no solicitudes que el padre ya haya iniciado.
   */
  const scheduleAsyncSearch = (value) => {
    cancelPendingSearch()
    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null
      submitAsyncSearch(value)
    }, ASYNC_SEARCH_DEBOUNCE_MS)
  }

  const clearSearch = () => {
    cancelPendingSearch()
    setQuery('')
    submitAsyncSearch('')
  }

  useEffect(
    () => () => {
      clearTimeout(searchTimeoutRef.current)
    },
    []
  )

  // El campo se reinicia al reabrir el selector, pero la lista del servidor puede
  // seguir mostrando los resultados de la búsqueda anterior.
  useEffect(() => {
    if (asyncSearch && initialRemoteSearchRef.current) submitAsyncSearch('')
  }, [asyncSearch])

  const visibleOptions = useMemo(() => {
    if (asyncSearch) return options

    const normalizedQuery = normalizeSearch(query)
    return normalizedQuery
      ? options.filter((option) => normalizeSearch(option.label).includes(normalizedQuery))
      : options
  }, [asyncSearch, options, query])

  useEffect(() => {
    if (!loading) loadRequestedRef.current = false
  }, [loading, options.length])

  const handleOptionsScroll = (event) => {
    if (!hasMore || loading || loadRequestedRef.current || typeof onLoadMore !== 'function') return

    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    if (scrollHeight - (scrollTop + clientHeight) <= 48) {
      loadRequestedRef.current = true
      onLoadMore()
    }
  }
  const restoringOptions = asyncSearch && !query.trim() && Boolean(remoteSearchQuery.trim())
  const displayedOptions = restoringOptions ? [] : visibleOptions
  const visibleValues = displayedOptions.map((option) => option.value)
  const globallySelected = globalSelection && (selection?.mode === 'exclude' || selection?.explicit)
  const isSelected = (value) => globallySelected ? !(selection.values || []).includes(value) : selectedValues.includes(value)
  const selectedVisibleCount = displayedOptions.filter((option) => isSelected(option.value)).length
  const allVisibleSelected =
    displayedOptions.length > 0 && selectedVisibleCount === displayedOptions.length
  const someVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < displayedOptions.length

  return (
    <div className="filter-ubiqua-mui-option-picker">
      {showSearch && (
        <div className="filter-ubiqua-mui-picker-search">
          <MagnifyingGlass size={14} weight="regular" aria-hidden="true" />
          <input
            disabled={disabled}
            type="search"
            autoFocus={autoFocus}
            value={query}
            onChange={(event) => {
              const nextQuery = event.target.value
              setQuery(nextQuery)
              if (!nextQuery.trim()) {
                cancelPendingSearch()
                submitAsyncSearch('')
              } else if (asyncSearch && !searchOnEnter) {
                scheduleAsyncSearch(nextQuery)
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                cancelPendingSearch()
                submitAsyncSearch(event.currentTarget.value)
              }
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            autoComplete="off"
          />
          {query ? (
            <button
              type="button"
              className="filter-ubiqua-mui-picker-search-clear"
              onClick={clearSearch}
              aria-label="Limpiar búsqueda del filtro"
              title="Limpiar búsqueda"
            >
              <X size={14} weight="bold" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      )}
      <div className="filter-ubiqua-mui-picker-select-all">
        {singleSelect ? (
          <span className="filter-ubiqua-mui-single-hint">Selecciona una opción</span>
        ) : (
          <label>
            <input
              type="checkbox"
              className="filter-ubiqua-mui-check"
              checked={
                globalSelection
                  ? Boolean(globallySelected && selection?.mode === 'all')
                  : allVisibleSelected
              }
              ref={(node) => {
                if (node)
                  node.indeterminate = globalSelection
                    ? selection?.mode === 'exclude' || selectedValues.length > 0
                    : someVisibleSelected
              }}
              disabled={disabled || optionsDisabled || !displayedOptions.length}
              onChange={(event) =>
                onSelectAllVisible?.(filterKey, visibleValues, event.target.checked)
              }
            />
            <span>
              {globalSelection
                ? selectAllLabel
                : query.trim() || hasMore
                  ? 'Seleccionar esta lista'
                  : 'Seleccionar todos'}
            </span>
          </label>
        )}
        {query || selectedValues.length > 0 || globallySelected ? (
          <button
            type="button"
            className="filter-ubiqua-mui-text-btn"
            disabled={disabled}
            onClick={() => {
              if (query) clearSearch()
              else onClear?.(filterKey, [])
            }}
          >
            {query ? 'Limpiar búsqueda' : 'Limpiar selección'}
          </button>
        ) : null}
      </div>
      <div
        className="filter-ubiqua-mui-option-list"
        data-scrollable={displayedOptions.length > 5 ? 'true' : 'false'}
        onScroll={handleOptionsScroll}
      >
        {displayedOptions.map((option, optionIndex) => {
          const selected = isSelected(option.value)
          return (
            <label
              key={option.value}
              className={`filter-ubiqua-mui-option-row ${selected ? 'is-checked' : ''}`}
            >
              <input
                type={singleSelect ? 'radio' : 'checkbox'}
                name={singleSelect ? radioGroupId : undefined}
                className="filter-ubiqua-mui-check"
                disabled={disabled || optionsDisabled}
                autoFocus={!showSearch && autoFocus && (selectedValues.length ? option.value === selectedValues[0] : optionIndex === 0)}
                checked={selected}
                onChange={() => onToggle?.(filterKey, option.value)}
              />
              <span className="filter-ubiqua-mui-option-copy">{option.label}</span>
              {Number.isFinite(option.count) ? (
                <span className="filter-ubiqua-mui-option-count">{option.count}</span>
              ) : null}
            </label>
          )
        })}
        {error ? (
          <div role="alert" className="filter-ubiqua-mui-picker-empty">
            <p>{error}</p>
            <button type="button" onClick={onRetry} disabled={loading}>
              Volver a intentar
            </button>
          </div>
        ) : null}
        {!displayedOptions.length && !loading && !restoringOptions && !error ? (
          <p className="filter-ubiqua-mui-picker-empty">{emptyLabel}</p>
        ) : null}
        {showLoadingIndicator && (loading || restoringOptions) ? (
          <p className="filter-ubiqua-mui-picker-empty">Cargando…</p>
        ) : null}
      </div>
    </div>
  )
}
